import React, { createContext, useContext, useState, useEffect } from 'react';
import * as authService from '../services/authService';
import { reinitializeApi } from '../services/api';

// Create the Auth context
const AuthContext = createContext();

// Auth context provider component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null); // Store original user when masquerading
  const [isMasquerading, setIsMasquerading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [otpData, setOTPData] = useState(null);
  const [showOTPForm, setShowOTPForm] = useState(false);

  // Efficient user data comparison function to prevent unnecessary re-renders
  const hasUserDataChanged = (currentUser, newUser) => {
    if (!currentUser || !newUser) return true;
    
    // Compare critical fields that affect app functionality
    const criticalFields = [
      'user_id', 'user_full_name', 'user_email', 'role', 'department',
      'permissions', 'user_permissions', 'approver', 'supervisor', 
      'manager', 'hod', 'director', 'role_name'
    ];
    
    for (const field of criticalFields) {
      const currentValue = JSON.stringify(currentUser[field]);
      const newValue = JSON.stringify(newUser[field]);
      if (currentValue !== newValue) {
        return true;
      }
    }
    return false;
  };

  // Listen for user data changes from API interceptor (immediate updates)
  useEffect(() => {
    const handleUserDataChanged = (event) => {
      const freshUser = event.detail.user; 
      setUser(freshUser);
      // Reinitialize API when user data changes to ensure auth headers are updated
      reinitializeApi();
    };

    const handleAuthLogout = () => {
      console.log('🚪 AuthContext received auth:logout event');
      authService.logout();
    };

    window.addEventListener('userDataChanged', handleUserDataChanged);
    window.addEventListener('auth:logout', handleAuthLogout);
    
    return () => {
      window.removeEventListener('userDataChanged', handleUserDataChanged);
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, []); // Empty dependency - event listener is set once

  // Periodic session refresh to keep user data and permissions fresh
  useEffect(() => {
    // Skip for MMS mobile routes - they use their own token system
    if (window.location.pathname.startsWith('/mms-mobile')) {
      return;
    }

    // Only set up refresh interval if user is authenticated
    if (!user) return;
  
    // Refresh session every 10 minutes to ensure permissions stay fresh while minimizing UX disruption
    // INCREASED FROM 2 MINUTES: Balance between data freshness and user experience
    const refreshInterval = setInterval(async () => {
      try {
         
        // CRITICAL: Skip session refresh if masquerading is active
        // Otherwise, the refresh will overwrite the masqueraded user with the original user from the server
        if (isMasquerading) {
          return;
        }
        
        const userProfile = await authService.verifySession();
        
        if (userProfile && userProfile.status === 1) {
          const freshUser = userProfile.user || userProfile;
          
          // Only update state if user data has actually changed to prevent unnecessary re-renders
          if (hasUserDataChanged(user, freshUser)) {
            console.log('🔄 AuthContext: User data changed, updating state');
            setUser(freshUser);
            localStorage.setItem('user', JSON.stringify(freshUser));
          }
        } else if (userProfile && userProfile.status === 0) {
          authService.logout();
        }
      } catch (err) {
        // Don't logout on refresh failures - might be temporary network issue
      }
    }, 10 * 60 * 1000); // 10 minutes - balanced approach for better UX

    // Cleanup interval on unmount or when user changes
    return () => { 
      clearInterval(refreshInterval);
    };
  }, [user?.user_id, isMasquerading]); // Re-setup when user changes or masquerading state changes

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try { 
        // Skip auth initialization for MMS mobile routes - they use their own token system
        if (window.location.pathname.startsWith('/mms-mobile')) {
          setLoading(false);
          return;
        }

        // Force API reinitialization to ensure correct base URL
        reinitializeApi();
        
        const token = localStorage.getItem('token');
        const masqueradeData = localStorage.getItem('masquerading');
        
        if (token) {
          // Check for masquerading state in localStorage first
          if (masqueradeData) {
            try {
              const masqueradeState = JSON.parse(masqueradeData);
              if (masqueradeState.isMasquerading && masqueradeState.originalUser && masqueradeState.targetUser) {
                // Handle nested user data structure
                let targetUser = masqueradeState.targetUser;
                if (targetUser && targetUser.row) {
                  targetUser = targetUser.row;
                }
                
                setOriginalUser(masqueradeState.originalUser);
                setUser(targetUser);
                setIsMasquerading(true);
                setLoading(false);
                return;
              }
            } catch (error) {
              localStorage.removeItem('masquerading');
            }
          }

          // Optimistic restore: use cached user to keep app logged-in until server check completes
          const cachedUserStr = localStorage.getItem('user');
          if (cachedUserStr) {
            try {
              const cachedUser = JSON.parse(cachedUserStr);
              setUser(cachedUser);
            } catch (err) {
            }
          }

          // Background server session verification
          let userProfile = null;
          try {
            userProfile = await authService.verifySession();
          } catch (verifyErr) {
            userProfile = null;
          }

          if (userProfile && userProfile.status === 1) {
            // CRITICAL: Always update user data from server to ensure fresh permissions
            const freshUser = userProfile.user || userProfile;
      
            // Compare with cached data to detect changes
            const cachedUser = cachedUserStr ? JSON.parse(cachedUserStr) : null;
            if (cachedUser) {
              const cachedPerms = JSON.stringify(cachedUser.permissions || []);
              const freshPerms = JSON.stringify(freshUser.permissions || []);
            }
            
            setUser(freshUser);
            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(freshUser));
          } else if (userProfile && userProfile.status === 0) {
            // Server explicitly reports no active session - clear client state 
            try {
              authService.logout();
            } catch (logoutErr) {
              // Don't let logout errors crash the app
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setUser(null);
            }
          } else {
            // userProfile === null -> network or CORS issue; keep cached user and do not logout
          }
        }
      } catch (err) {
        // Don't let auth initialization errors crash the app
        try {
          authService.logout();
        } catch (logoutErr) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      } finally {
        setLoading(false); 
      }
    };

    initAuth();
  }, []);

  // Microsoft login function
  const loginWithMicrosoft = async () => {
    setLoading(true);
    setError(null);
    try {
      await authService.loginWithMicrosoft();
      // No need to set user here as it will be handled by callback
    } catch (err) {
      setError('Failed to initiate Microsoft login');
      setLoading(false);
      throw err;
    }
  };

  // Regular login with username/password
  const login = async (username, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.loginWithUsername(username, password);
      
      if (response.status === 1) {
        // Complete login
        setUser(response.user);
        setOTPData(null);
        setShowOTPForm(false);
        return response;
      } else if (response.status === 'otp_required') {
        // OTP verification required
        const otpDataToSet = {
          otpToken: response.otp_token,
          userPhoneMasked: response.user_phone_masked,
          userEmailMasked: response.user_email_masked,
          otpMethod: response.otp_method,
          message: response.message
        };
        setOTPData(otpDataToSet);
        setShowOTPForm(true);
        return response;
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      return null; // Return null to indicate failure
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and complete login
  const verifyOTP = async (otp, otpToken) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.verifyOTP(otp, otpToken);
      
      if (response && response.status === 1) {
        setUser(response.user);
        setOTPData(null);
        setShowOTPForm(false);
        setError(null);
        return response;
      } else {
        // OTP verification failed - keep OTP form open and show error
        const errorMessage = (response && response.message) || 'Invalid OTP. Please try again.';
        setError(errorMessage);
        return null;
      }
    } catch (err) {
      // Don't logout on OTP verification failure - keep the OTP form open
      const errorMessage = err.response?.data?.message || err.message || 'OTP verification failed. Please try again.';
      setError(errorMessage);
      return null; // Return null to indicate failure
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async (otpToken) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.resendOTP(otpToken);
      
      if (response.status !== 1) {
        throw new Error(response.message || 'Failed to resend OTP');
      }
      return response;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to resend OTP.';
      setError(errorMessage);
      return null; // Return null to indicate failure
    } finally {
      setLoading(false);
    }
  };

  // Login with approval token (one-time link authentication)
  const loginWithApprovalToken = async (approvalToken, ticketId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authService.loginWithApprovalToken(approvalToken, ticketId);
      
      if (response && response.status === 1) {
        setUser(response.user);
        setError(null);
        return response;
      } else {
        throw new Error(response?.message || 'Invalid or expired approval link');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Authentication failed. The approval link may be invalid or expired.';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setUser(null);
  };

  // Function to update user data in the context
  const updateUser = (newUserData) => {
    setUser(newUserData);
    // Also update localStorage if you are persisting the user object there
    localStorage.setItem('user', JSON.stringify(newUserData));
  };

  // Masquerading functions - now using backend API
  const startMasquerading = async (targetUserId) => {
    if (!user || (user.user_id !== 1 && user.user_id !== '1')) {
      throw new Error('Only user ID 1 can masquerade as other users');
    }

    try {
      const response = await authService.startMasquerading(targetUserId);
      if (response.status === 1) {
        // Handle nested user data structure
        let targetUser = response.targetUser;
        if (targetUser && targetUser.row) {
          targetUser = targetUser.row;
        }
        
        // console.log('🎭 AuthContext.startMasquerading: Setting target user:', {
        //   targetUserId: targetUser.user_id,
        //   targetUserName: targetUser.user_full_name || targetUser.user_name,
        //   targetUserPermissions: targetUser.permissions || targetUser.user_permissions,
        //   originalUserId: response.originalUser.user_id,
        //   originalUserName: response.originalUser.user_full_name || response.originalUser.user_name
        // });
        
        // Update state immediately - localStorage is already updated by authService
        setOriginalUser(response.originalUser);
        setUser(targetUser);
        setIsMasquerading(true);
        
        //console.log('✅ Started masquerading as (AuthContext):', targetUser.user_full_name);
        
        return response;
      } else {
        throw new Error(response.message || 'Failed to start masquerading');
      }
    } catch (err) {
      throw err;
    }
  };

  const stopMasquerading = async () => {
    if (!isMasquerading || !originalUser) {
      throw new Error('Not currently masquerading');
    }

    try {
      const response = await authService.stopMasquerading();
      if (response.status === 1) {
        // Update state immediately - localStorage is already updated by authService
        setUser(response.originalUser);
        setOriginalUser(null);
        setIsMasquerading(false);
        
        //console.log('✅ Stopped masquerading, returned to (AuthContext):', response.originalUser.user_full_name);
        
        return response;
      } else {
        throw new Error(response.message || 'Failed to stop masquerading');
      }
    } catch (err) {
      throw err;
    }
  };

  const canMasquerade = () => {
    return user && (user.user_id === 1 || user.user_id === '1');
  };

  // Clear OTP state function
  const clearOTPState = () => {
    setOTPData(null);
    setShowOTPForm(false);
    setError(null);
  };

  // Context value
  const contextValue = {
    user,
    originalUser,
    isMasquerading,
    loading,
    error,
    otpData,
    showOTPForm,
    isAuthenticated: !!user,
    loginWithMicrosoft,
    login,
    verifyOTP,
    resendOTP,
    loginWithApprovalToken,
    logout,
    updateUser,
    startMasquerading,
    stopMasquerading,
    canMasquerade,
    clearOTPState,
    // Add a permissions helper
    can: (permission) => {
      if (!user) return false;
      
      // Check multiple possible permission fields
      const permissionField = user.permissions || user.user_permissions; 
      
      if (!permissionField) {
        console.log('🔑 can() - No permissions field found for user:', user?.user_id);
        return false;
      }
      
      try {
        let permissions;
        
        // Check if it's a PHP serialized array
        if (typeof permissionField === 'string' && permissionField.startsWith('a:')) {
          // For now, let's handle the common case where we can extract permissions
          // This is a simplified parser for the specific format we're seeing
          const matches = permissionField.match(/s:\d+:"([^"]+)"/g);
          if (matches) {
            permissions = matches.map(match => {
              const content = match.match(/s:\d+:"([^"]+)"/);
              return content ? content[1] : null;
            }).filter(Boolean);
          } else {
            permissions = [];
          }
        } else if (typeof permissionField === 'string') {
          // Try to parse as JSON
          permissions = JSON.parse(permissionField);
        } else {
          permissions = permissionField;
        }
      
        return Array.isArray(permissions) && permissions.includes(permission);
      } catch (error) {
        console.log('🔑 can() - Error parsing permissions:', error);
        return false;
      }
    },
    // Add a role helper
    hasRole: (role) => {
      if (!user) return false;
      // Check both role_name (string) and role (number)
      if (user.role_name && user.role_name === role) return true;
      if (user.role && user.role.toString() === role) return true;
      return false;
    },
    
    // User info helpers (from Utility.php)
    getUserId: () => user?.user_id || 0,
    getUserName: () => user?.user_full_name || '',
    getUserEmail: () => user?.user_email || '',
    getFullName: () => user?.user_full_name || '',
    getFirstName: () => {
      if (user?.user_full_name) {
        const parts = user.user_full_name.split(' ');
        return parts[0] !== '' ? parts[0] : user.user_full_name;
      }
      return '';
    },
    getRole: () => user?.role || 0,
    getDepartment: () => user?.department || 0,
    
    // Role checking helpers (from Utility.php)
    isStaff: () => {
      if (!user) return false;
      // Staff is anyone who is NOT supervisor, manager, hod, or director
      const isSuper = user.supervisor === 1 || user.supervisor === '1';
      const isMgr = user.manager === 1 || user.manager === '1';
      const isHodRole = user.hod === 1 || user.hod === '1';
      const isDir = user.director === 1 || user.director === '1';
      return !isSuper && !isMgr && !isHodRole && !isDir;
    },
    
    isSupervisor: () => {
      if (!user) return false;
      return user.supervisor === 1 || user.supervisor === '1';
    },
    
    isManager: () => {
      if (!user) return false;
      return user.manager === 1 || user.manager === '1';
    },
    
    isHod: () => {
      if (!user) return false;
      return user.hod === 1 || user.hod === '1';
    },
    
    isDirector: () => {
      if (!user) return false;
      return user.director === 1 || user.director === '1';
    },
    
    isApprover: () => {
      if (!user) return false;
      // Handle different data types for approver flag - be more flexible
      const approverValue = user.approver;
      return approverValue === true || approverValue === 1 || approverValue === '1' || approverValue === 'true';
    },
    
    // Department checking helpers (from Utility.php)
    isIT: () => {
      if (!user) return false;
      return user.department === 27 || user.department === '27';
    },
    
    isHR: () => {
      if (!user) return false;
      return user.department === 25 || user.department === '25';
    },
    
    isCallCenter: () => {
      if (!user) return false;
      return user.department === 1 || user.department === '1';
    },
    
    isProcurement: () => {
      if (!user) return false;
      return user.department === 8 || user.department === '8';
    },
    
    isFinance: () => {
      if (!user) return false;
      return user.department === 22 || user.department === '22';
    },
    
    // Default password check (from Utility.php)
    isDefault: () => {
      if (!user || !user.user_password) return false;
      return user.user_password.substr(-3) === '321';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
